import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FolderOpen, PlusCircle, FileText, MessageSquare, CreditCard, LogOut, Shield, Landmark } from "lucide-react";
import { Logo } from "../components/site/Logo";
import { useAuth } from "../context/AuthContext";

const nav = [
  { to: "/area", end: true, icon: LayoutDashboard, label: "Dashboard", testid: "area-nav-dashboard" },
  { to: "/area/pratiche", icon: FolderOpen, label: "Pratiche", testid: "area-nav-pratiche" },
  { to: "/area/richieste", icon: PlusCircle, label: "Richieste", testid: "area-nav-richieste" },
  { to: "/area/documenti", icon: FileText, label: "Documenti", testid: "area-nav-documenti" },
  { to: "/area/messaggi", icon: MessageSquare, label: "Messaggi", testid: "area-nav-messaggi" },
  { to: "/area/piano", icon: CreditCard, label: "Piano", testid: "area-nav-piano" },
  { to: "/area/credito", icon: Landmark, label: "Credito", testid: "area-nav-credito" },
];

export default function AreaLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      {/* Sidebar desktop */}
      <aside className="hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="border-b border-slate-200 px-6 py-5">
          <Logo />
        </div>
        <nav className="flex-1 space-y-1 p-4">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                  isActive ? "bg-[#10B981] text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4">
          {user?.role === "admin" && (
            <button data-testid="area-to-admin" onClick={() => navigate("/admin")} className="mb-1 flex w-full items-center gap-3 rounded-xl bg-[#0A192F] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800">
              <Shield className="h-5 w-5" /> Pannello Back Office
            </button>
          )}
          <button data-testid="area-logout" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100">
            <LogOut className="h-5 w-5" /> Esci
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white/80 px-6 py-4 backdrop-blur-xl">
          <div className="lg:hidden"><Logo /></div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-[#0A192F]" data-testid="area-user-name">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0A192F] font-display text-sm font-bold text-white">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </span>
          </div>
        </header>

        <main className="flex-1 px-6 py-8 pb-24 lg:px-10 lg:pb-8">
          <Outlet />
        </main>
      </div>

      {/* Bottom nav mobile */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-center gap-1 overflow-x-auto border-t border-slate-200 bg-white px-2 py-2 lg:hidden">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`${n.testid}-mobile`}
            className={({ isActive }) =>
              `flex min-w-[64px] flex-shrink-0 flex-col items-center gap-0.5 rounded-lg px-2 py-1 text-[10px] font-semibold transition-colors ${
                isActive ? "text-[#10B981]" : "text-slate-500"
              }`
            }
          >
            <n.icon className="h-5 w-5" /> {n.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
